import { useEffect, useState } from 'react';
import { Box, Button, Flex, Icon, Text, Badge, Spacer } from '@chakra-ui/react';
import {
  PlusCircleIcon,
  ArrowRightIcon,
  CheckIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { jwtDecode } from 'jwt-decode';
import { useNavigate, NavLink } from 'react-router-dom';
import { MenuContent, MenuItem, MenuRoot, MenuTrigger, MenuSeparator } from '@/components/ui/menu';
import { toaster } from '@/components/ui/toaster';
import CreateAccountModal from '@/pages/workspace/create-account-modal';
import useWorkspace from '@/lib/hooks/use-workspace';
import useWorkspaceStore from '@/utils/use-workspace';
import { useAuth } from '@/lib/hooks/use-auth';
import useModalToggle from '@/lib/hooks/use-modal-toggle';
import { AdminRoles, ExtendedWorkspace, SportType } from '@/lib/types';
import api from '@/utils/api';
import { orderWorkspaces } from '@/lib/helpers';

interface Props {
  currentWorkspaceId: string | undefined;
}

interface DecodedToken {
  role_id: number;
}

const WorkspaceMenu = ({ currentWorkspaceId }: Props) => {
  const navigate = useNavigate();
  const { workspaces, loadWorkspaces } = useWorkspaceStore();
  const { workspace } = useWorkspace();
  const { changeWorkspace, changeRole } = useAuth();
  const createModal = useModalToggle();
  const [sportTypes, setSportTypes] = useState<Map<number, string>>(new Map());

  const items = currentWorkspaceId ? orderWorkspaces(workspaces, currentWorkspaceId) : [];

  const handleWorkspace = async (workspace: ExtendedWorkspace) => {
    try {
      if (checkCurrentWorkspace(workspace.id)) return;

      const response = await api.get(`workspace/${workspace.id}/token`);
      if (response.data.resource) {
        const decoded = jwtDecode<DecodedToken>(response.data.resource);
        localStorage.setItem('accessToken', response.data.resource);
        changeWorkspace(workspace);
        changeRole({
          id: decoded?.role_id,
          name: workspace.role_name,
        });
        navigate(`/${workspace.id}/home`);
      }
    } catch (error: any) {
      toaster.create({
        type: 'error',
        title: error.response?.data?.error || 'Failed to change workspace',
      });
    }
  };

  function checkCurrentWorkspace(id: number) {
    return currentWorkspaceId && parseFloat(currentWorkspaceId) === id;
  }

  useEffect(() => {
    async function fetchSportTypes() {
      const response = await api.get(`/public/sport-types`);
      const map = new Map<number, string>();
      response.data.resource.forEach((item: SportType) => {
        map.set(item.id, item.name);
      });
      setSportTypes(map);
    }
    loadWorkspaces();
    fetchSportTypes();
  }, [loadWorkspaces]);

  return (
    <>
      <MenuRoot>
        <MenuTrigger asChild>
          <Flex
            as='button'
            align='center'
            gap={2}
            minW={0}
            pl={{ md: 4 }}
            borderWidth='0'
            borderLeftWidth={{ md: '1px' }}
            borderLeftStyle='solid'
            borderColor='border'
            bg='transparent'
            appearance='none'
            cursor='pointer'
            fontSize='sm'
            fontWeight='600'
            color='fg.strong'
            transition='color .12s'
            _hover={{ color: 'brand.solid' }}
          >
            <Text
              maxW={{ base: '100px', md: '180px' }}
              overflow='hidden'
              textOverflow='ellipsis'
              whiteSpace='nowrap'
            >
              {workspace?.name || 'Workspace'}
            </Text>
            <Icon as={ChevronDownIcon} w={4} h={4} color='fg.muted' />
          </Flex>
        </MenuTrigger>
        <MenuContent minW={350}>
          {items.map(
            (row, index) =>
              index < 5 && (
                <MenuItem
                  key={row.id}
                  value={row.name}
                  minH={'36px'}
                  bg={checkCurrentWorkspace(row.id) ? 'brand.100' : 'white'}
                  _hover={
                    checkCurrentWorkspace(row.id)
                      ? {
                          bg: 'brand.50',
                        }
                      : {
                          bg: 'brand.50',
                        }
                  }
                  onClick={() => handleWorkspace(row)}
                >
                  <Flex w={'full'} alignItems='center' flexWrap='wrap'>
                    {checkCurrentWorkspace(row.id) && <Icon as={CheckIcon} w={4} h={4} mr={3} />}
                    <Box>
                      {row.name}
                      {row.sport_type_id && sportTypes.has(row.sport_type_id) && (
                        <Badge variant='surface' ml={2}>
                          {sportTypes.get(row.sport_type_id)}
                        </Badge>
                      )}
                    </Box>
                    <Spacer />
                    {row.role_id === AdminRoles.WorkspaceAdmin && checkCurrentWorkspace(row.id) && (
                      <NavLink to={`/${row.id}/account/edit`}>
                        <Button colorPalette='brand' variant='surface' size='xs' height={6}>
                          Edit
                        </Button>
                      </NavLink>
                    )}
                  </Flex>
                </MenuItem>
              ),
          )}
          {items.length >= 5 && (
            <MenuItem value='more-workspaces' minH={'36px'} asChild>
              <NavLink to={`/${currentWorkspaceId}/workspaces`}>
                <Box>More workspaces</Box>
                <Icon as={ArrowRightIcon} w={4} h={4} />
              </NavLink>
            </MenuItem>
          )}
          <MenuSeparator />
          <MenuItem value='create-new-workspace' minH={'36px'} onClick={createModal.open}>
            <Flex w={'full'} alignItems='center' flexWrap='wrap'>
              <Icon as={PlusCircleIcon} w={4} h={4} mr={3} />
              <Box flex='1'>Create new workspace</Box>
            </Flex>
          </MenuItem>
        </MenuContent>
      </MenuRoot>

      <CreateAccountModal {...createModal.props} onSuccess={() => navigate(0)} />
    </>
  );
};

export default WorkspaceMenu;
